import React, { forwardRef } from 'react';
import { Phone } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';

export interface PhoneButtonProps extends Omit<ButtonProps, 'onClick' | 'children' | 'type'> {
  phoneNumber: string;
  formatted: string;
  label: string;
  description?: string;
  showIcon?: boolean;
  showNumber?: boolean;
  phoneType?: 'sales' | 'support';
  compact?: boolean;
  trackingContext?: string;
  context?: 'navbar' | 'footer' | 'sticky' | 'default';
}

/**
 * High-converting phone button component optimized for mobile and desktop
 */
export const PhoneButton = forwardRef<HTMLButtonElement, PhoneButtonProps>(
  ({ 
    phoneNumber, 
    formatted, 
    label, 
    description,
    showIcon = true,
    showNumber = true,
    phoneType = 'sales',
    compact = false,
    trackingContext = 'unknown',
    context = 'default',
    className,
    variant = context === 'navbar' ? 'ghost' : phoneType === 'sales' ? 'phone-sales' : 'phone-support',
    size = compact ? 'sm' : 'default',
    ...props 
  }, ref) => {
    
    const handlePhoneClick = () => {
      // Track phone click for analytics
      logger.userAction('phone_click', {
        phoneNumber,
        phoneType,
        context: trackingContext,
        timestamp: new Date().toISOString()
      });

      // Open phone dialer
      window.location.href = phoneNumber;
    };

    const phoneClasses = cn(
      'group relative overflow-hidden transition-all duration-300',
      className
    );

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        onClick={handlePhoneClick}
        className={phoneClasses}
        {...props}
      >
        <div className={cn(
          'flex items-center gap-2',
          !compact && 'flex-col sm:flex-row text-center sm:text-left'
        )}>
          {showIcon && (
            <Phone className={cn(
              'transition-all duration-300 group-hover:animate-pulse',
              compact ? 'h-4 w-4' : 'h-5 w-5',
              context === 'navbar' && phoneType === 'sales' && 'text-edgefi-purple',
              context === 'navbar' && phoneType === 'support' && 'text-edgefi-blue',
              context !== 'navbar' && phoneType === 'sales' && 'text-white drop-shadow-sm',
              context !== 'navbar' && phoneType === 'support' && 'text-edgefi-blue group-hover:text-white'
            )} />
          )}
          
          <div className={cn('flex flex-col', compact && 'items-start')}>
            {!compact && (
              <span className={cn(
                "text-xs font-medium tracking-wide uppercase",
                context === 'navbar' && phoneType === 'sales' && 'text-edgefi-purple/80 group-hover:text-white/90',
                context === 'navbar' && phoneType === 'support' && 'text-edgefi-blue/80 group-hover:text-white/90',
                context !== 'navbar' && phoneType === 'sales' && 'text-white/90',
                context !== 'navbar' && phoneType === 'support' && 'text-edgefi-blue/80 group-hover:text-white/90'
              )}>
                {label}
              </span>
            )}
            
            {showNumber && (
              <span className={cn(
                'font-bold tracking-tight',
                compact ? 'text-sm' : 'text-base',
                context === 'navbar' && phoneType === 'sales' && 'text-edgefi-purple',
                context === 'navbar' && phoneType === 'support' && 'text-edgefi-blue',
                context !== 'navbar' && phoneType === 'sales' && 'text-white',
                context !== 'navbar' && phoneType === 'support' && 'text-edgefi-blue group-hover:text-white'
              )}>
                {formatted}
              </span>
            )}
            
            {description && !compact && (
              <span className={cn(
                "text-xs font-medium mt-1 transition-colors duration-300",
                context === 'navbar' && phoneType === 'sales' && 'text-edgefi-purple/70 group-hover:text-white/80',
                context === 'navbar' && phoneType === 'support' && 'text-edgefi-blue/70 group-hover:text-white/80',
                context !== 'navbar' && phoneType === 'sales' && 'text-white/80',
                context !== 'navbar' && phoneType === 'support' && 'text-edgefi-blue/70 group-hover:text-white/80'
              )}>
                {description}
              </span>
            )}
          </div>
        </div>
      </Button>
    );
  }
);

PhoneButton.displayName = 'PhoneButton';

export default PhoneButton;